import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useApiQuery } from '../../../../../api/hooks';
import apiClient from '../../../../../api/apiClient';
import { usePreloaderStore, useConfirmationModalStore } from '../../../../../stores';
import { PlusIcon, DownloadIcon, SettingsIcon, BellIcon } from '../../../model/icons';
import TaskCard from './TaskCard';
import TaskModal from './TaskModal';
import CategoryModal from './CategoryModal';
import SimpleNameModal from './SimpleNameModal';
import ImportModal from './ImportModal';
import ChallengeModal from './challenges/ChallengeModal';
import { CHALLENGE_FLAGGED_KEY } from './challenges/challengeKeys';
import { CHALLENGE_HOME_NAME } from './challenges/challengeUtils';

// Псевдо-категория «Челленджи»: собирает все задания isChallenge, механика отдельная (эстафета + модерация).
const CHALLENGES_CAT = { id: '__challenges__', displayName: 'Челленджи', pseudo: true };

// Панель «Задания»: категории/подкатегории (чипы) + сетка заданий.
export default function TasksPanel({ neighbor, smenaId }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const { open: openConfirm } = useConfirmationModalStore();
    const { data: categories = [], refetch: refetchCategories } = useApiQuery(
        ['neighbor', 'task-categories', neighbor.id],
        `/api/neighbor/admin/task-categories/list-by-neighbor?neighborId=${neighbor.id}`,
        { refetchOnMount: 'always' }
    );
    const { data: tasks = [], refetch: refetchTasks } = useApiQuery(
        ['neighbor', 'tasks', neighbor.id],
        `/api/neighbor/admin/tasks/list-by-neighbor?neighborId=${neighbor.id}`,
        { refetchOnMount: 'always' }
    );
    const [selCat, setSelCat] = useState(null);
    const [selSub, setSelSub] = useState(null);
    const isChallenges = selCat?.pseudo === true;
    const { data: subcategories = [], refetch: refetchSubs } = useApiQuery(
        ['neighbor', 'task-subcategories', selCat?.id],
        `/api/neighbor/admin/task-subcategories/list-by-category?categoryId=${selCat?.id}`,
        { enabled: !!selCat?.id && !isChallenges, refetchOnMount: 'always' }
    );

    // Звенья челленджей «на проверке» (эндпоинт scope по X-Camp-Id), сгруппированные по taskId.
    const { data: flaggedSteps = [], refetch: refetchFlagged } = useApiQuery(
        CHALLENGE_FLAGGED_KEY(smenaId),
        '/api/neighbor/admin/challenges/steps/flagged',
        { enabled: !!smenaId, refetchOnMount: 'always' }
    );
    const flaggedByTask = useMemo(() => {
        const map = {};
        (flaggedSteps || []).forEach(f => { (map[f.taskId] = map[f.taskId] || []).push(f); });
        return map;
    }, [flaggedSteps]);
    const flaggedTotal = flaggedSteps?.length || 0;

    // Скрытая категория-дом челленджей: пока она есть, вкладка «Челленджи» видна даже без заданий (можно создать новые или удалить всё).
    const challengeHomeCat = useMemo(() => categories.find(c => c.displayName === CHALLENGE_HOME_NAME), [categories]);
    const hasChallenges = useMemo(() => tasks.some(t => t.isChallenge) || !!challengeHomeCat, [tasks, challengeHomeCat]);
    // Скрываем системную категорию челленджей и категории, где все задания — челленджи (пустые оставляем — в них добавляют задания).
    const visibleCategories = useMemo(() => categories.filter(cat => {
        if (cat.displayName === CHALLENGE_HOME_NAME) return false;
        const catTasks = tasks.filter(t => t.categoryId === cat.id);
        return catTasks.length === 0 || catTasks.some(t => !t.isChallenge);
    }), [categories, tasks]);

    const [taskModal, setTaskModal] = useState(null);           // {task} | {create:true}
    const [challengeModal, setChallengeModal] = useState(null); // {task} | {create:true}
    const [catModal, setCatModal] = useState(null);         // {category} | {create:true}
    const [subModal, setSubModal] = useState(null);         // {subcategory} | {create:true}
    const [importOpen, setImportOpen] = useState(false);

    const filtered = isChallenges
        ? tasks.filter(t => t.isChallenge).sort((a, b) => (a.title || '').localeCompare(b.title || ''))
        : tasks
            .filter(t => selCat && !t.isChallenge && t.categoryId === selCat.id && (!selSub || t.subcategoryId === selSub.id))
            .sort((a, b) => (a.title || '').localeCompare(b.title || ''));

    const selectCategory = (cat) => { setSelCat(cat); setSelSub(null); };

    // Удаление всех челленджей = каскадное удаление скрытой категории-дома (бэк удалит и задания, и подкатегории).
    const deleteAllChallenges = () => openConfirm(
        <div className="confirm-msg">
            <b>Удалить все челленджи?</b>
            <p>Будут удалены все челленджи смены вместе со скрытой категорией импортированного пака. Действие необратимо.</p>
        </div>,
        async () => {
            show();
            try {
                await apiClient.delete(`/api/neighbor/admin/task-categories/delete?id=${challengeHomeCat.id}`);
                enqueueSnackbar('Челленджи удалены', { variant: 'success' });
                setSelCat(null);
                refetchCategories();
                refetchTasks();
            } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка при удалении', { variant: 'error' }); }
            finally { hide(); }
        },
        { confirmText: 'Удалить' }
    );

    const saveSub = async (name) => {
        show();
        try {
            if (subModal?.subcategory) {
                await apiClient.patch(`/api/neighbor/admin/task-subcategories/update?id=${subModal.subcategory.id}`, { displayName: name });
            } else {
                await apiClient.post('/api/neighbor/admin/task-subcategories/create', { categoryId: selCat.id, displayName: name });
            }
            enqueueSnackbar('Сохранено', { variant: 'success' });
            refetchSubs();
            setSubModal(null);
        } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка', { variant: 'error' }); }
        finally { hide(); }
    };
    const deleteSub = async () => {
        show();
        try {
            await apiClient.delete(`/api/neighbor/admin/task-subcategories/delete?id=${subModal.subcategory.id}`);
            enqueueSnackbar('Подкатегория удалена', { variant: 'success' });
            if (selSub?.id === subModal.subcategory.id) setSelSub(null);
            refetchSubs();
            setSubModal(null);
        } catch (e) { enqueueSnackbar('Ошибка при удалении', { variant: 'error' }); }
        finally { hide(); }
    };

    return (
        <div className="bt-panel">
            <section className="fr-card">
                <div className="sp-secthead">
                    <h2>Категории заданий</h2>
                    <div className="sp-headacts">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setImportOpen(true)}><span className="ic"><DownloadIcon /></span>Импорт из паков</button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => setCatModal({ create: true })}><span className="ic"><PlusIcon /></span>Категория</button>
                    </div>
                </div>
                {visibleCategories.length > 0 || hasChallenges ? (
                    <div className="sp-chips">
                        {visibleCategories.map(cat => (
                            <span key={cat.id} className={`sp-chip${selCat?.id === cat.id ? ' on' : ''}`} role="button" tabIndex={0} onClick={() => selectCategory(cat)} onKeyDown={(e) => { if (e.key === 'Enter') selectCategory(cat); }}>
                                {cat.notifyOrganizersOnPurchase && <span className="sp-chip-bell" title="Оргам уходит уведомление о покупке"><BellIcon /></span>}
                                {cat.displayName}
                                <button type="button" className="sp-chipgear" onClick={(e) => { e.stopPropagation(); setCatModal({ category: cat }); }}><SettingsIcon /></button>
                            </span>
                        ))}
                        {hasChallenges && (
                            <span className={`sp-chip${isChallenges ? ' on' : ''}`} role="button" tabIndex={0} onClick={() => selectCategory(CHALLENGES_CAT)} onKeyDown={(e) => { if (e.key === 'Enter') selectCategory(CHALLENGES_CAT); }}>
                                Челленджи
                                {flaggedTotal > 0 && <span className="sp-chip-flag">{flaggedTotal}</span>}
                            </span>
                        )}
                    </div>
                ) : <div className="bt-empty">Нет категорий</div>}

                {selCat && !isChallenges && (
                    <div className="sp-subbar">
                        <div className="sp-subbar-top">
                            <span className="sp-sublabel">Подкатегории</span>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSubModal({ create: true })}><span className="ic"><PlusIcon /></span>Подкатегория</button>
                        </div>
                        <div className="sp-chips">
                            <span className={`sp-chip${!selSub ? ' on' : ''}`} role="button" tabIndex={0} onClick={() => setSelSub(null)} onKeyDown={(e) => { if (e.key === 'Enter') setSelSub(null); }}>Все</span>
                            {subcategories.map(sub => (
                                <span key={sub.id} className={`sp-chip${selSub?.id === sub.id ? ' on' : ''}`} role="button" tabIndex={0} onClick={() => setSelSub(sub)} onKeyDown={(e) => { if (e.key === 'Enter') setSelSub(sub); }}>
                                    {sub.displayName}
                                    <button type="button" className="sp-chipgear" onClick={(e) => { e.stopPropagation(); setSubModal({ subcategory: sub }); }}><SettingsIcon /></button>
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </section>

            {selCat && !isChallenges && (
                <section className="fr-card">
                    <div className="sp-secthead">
                        <h2>Задания: {selCat.displayName}</h2>
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => setTaskModal({ create: true })}><span className="ic"><PlusIcon /></span>Создать задание</button>
                    </div>
                    {filtered.length > 0 ? (
                        <div className="sp-tasks">
                            {filtered.map((task, index) => (
                                <TaskCard key={task.id} task={task} index={index} onClick={() => setTaskModal({ task })} />
                            ))}
                        </div>
                    ) : <div className="bt-empty">Нет заданий в этой категории</div>}
                </section>
            )}

            {isChallenges && (
                <section className="fr-card">
                    <div className="sp-secthead">
                        <h2>Челленджи</h2>
                        <div className="sp-headacts">
                            {challengeHomeCat && (
                                <button type="button" className="btn btn-danger btn-sm" onClick={deleteAllChallenges}>Удалить все</button>
                            )}
                            <button type="button" className="btn btn-primary btn-sm" onClick={() => setChallengeModal({ create: true })}><span className="ic"><PlusIcon /></span>Создать челлендж</button>
                        </div>
                    </div>
                    {filtered.length > 0 ? (
                        <div className="sp-tasks">
                            {filtered.map((task, index) => (
                                <TaskCard
                                    key={task.id}
                                    task={task}
                                    index={index}
                                    flaggedCount={(flaggedByTask[task.id] || []).length}
                                    onClick={() => setChallengeModal({ task })}
                                />
                            ))}
                        </div>
                    ) : <div className="bt-empty">Нет челленджей</div>}
                </section>
            )}

            {taskModal && (
                <TaskModal
                    task={taskModal.task}
                    neighborId={neighbor.id}
                    smenaId={smenaId}
                    categories={categories}
                    defaultCategoryId={selCat?.id}
                    onSuccess={refetchTasks}
                    onClose={() => setTaskModal(null)}
                />
            )}
            {catModal && (
                <CategoryModal
                    category={catModal.category}
                    neighborId={neighbor.id}
                    onSuccess={() => { refetchCategories(); if (catModal.category && selCat?.id === catModal.category.id) setSelCat(null); }}
                    onClose={() => setCatModal(null)}
                />
            )}
            {subModal && (
                <SimpleNameModal
                    kicker="Подкатегория"
                    title={subModal.subcategory ? 'Редактировать подкатегорию' : 'Создать подкатегорию'}
                    label="Название подкатегории"
                    initial={subModal.subcategory?.displayName}
                    onSave={saveSub}
                    onDelete={subModal.subcategory ? deleteSub : null}
                    onClose={() => setSubModal(null)}
                />
            )}
            {importOpen && (
                <ImportModal smenaId={smenaId} onSuccess={() => { refetchCategories(); refetchTasks(); }} onClose={() => setImportOpen(false)} />
            )}
            {challengeModal && (
                <ChallengeModal
                    task={challengeModal.task}
                    neighborId={neighbor.id}
                    smenaId={smenaId}
                    flaggedSteps={challengeModal.task ? (flaggedByTask[challengeModal.task.id] || []) : []}
                    onSaved={refetchTasks}
                    onChanged={refetchFlagged}
                    onClose={() => setChallengeModal(null)}
                />
            )}
        </div>
    );
}

TasksPanel.propTypes = {
    neighbor: PropTypes.object.isRequired,
    smenaId: PropTypes.number
};

TasksPanel.defaultProps = { smenaId: null };
